"use client";

import { useEffect, useState } from 'react';
import { useAppearanceStore } from '@/stores/appearanceStore';
import { themeRadii, themeFonts, themeCardShadows } from '@/lib/theme/display-tokens';

function applyAccentColor(root: HTMLElement, color: string) {
  root.style.setProperty('--brand-primary', color);
  root.style.setProperty(
    '--brand-primary-hover',
    `color-mix(in srgb, ${color} 85%, black)`
  );
  root.style.setProperty(
    '--brand-primary-light',
    `color-mix(in srgb, ${color} 12%, white)`
  );
  root.style.setProperty(
    '--brand-primary-dark',
    `color-mix(in srgb, ${color} 65%, black)`
  );
  root.style.setProperty('--ring', color);
}

function getContrastForeground(hex: string): string {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance > 0.5 ? '#1a1f2e' : '#ffffff';
}

export function useWorkspaceTheme() {
  const { theme, density, accentColor: deviceAccent, sidebarColor: deviceSidebar, sidebarTextColor, contentBgColor: deviceContent, workspaceTheme, scope } = useAppearanceStore();
  // Botanical's colours (light AND dark) live in globals.css. Inline workspace
  // channels would outrank them, so only a person's own device override applies.
  const botanical = workspaceTheme?.palette === 'botanical';
  const accentColor = deviceAccent ?? (botanical ? undefined : workspaceTheme?.primaryColor);
  const sidebarColor = deviceSidebar ?? (botanical ? undefined : workspaceTheme?.sidebarColor);
  const contentBgColor = deviceContent ?? (botanical ? undefined : workspaceTheme?.contentBgColor);

  // Track effective dark state so per-channel overrides can defer to the .dark
  // block defaults instead of forcing a saved light value over them. Without
  // this gate the user's saved sidebarColor/contentBgColor (chosen for light
  // mode) get applied via inline style and override globals.css `.dark`,
  // producing the hybrid light-bg + dark-text mess Phase 921 shipped with.
  const [isDark, setIsDark] = useState(false);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const radius = workspaceTheme?.borderRadius && themeRadii[workspaceTheme.borderRadius];
    const font = !botanical && workspaceTheme?.fontFamily && themeFonts[workspaceTheme.fontFamily];
    const shadow = workspaceTheme?.cardStyle && themeCardShadows[workspaceTheme.cardStyle];
    if (typeof radius === 'string') root.style.setProperty('--radius', radius);
    else root.style.removeProperty('--radius');
    // next/font variables are attached to body, so font resolution must happen there.
    if (typeof font === 'string') body.style.setProperty('--font-sans', font);
    else body.style.removeProperty('--font-sans');
    if (typeof shadow === 'string') {
      root.style.setProperty('--card-border-width', workspaceTheme?.cardStyle === 'flat' ? '0px' : '1px');
      root.style.setProperty('--practice-card-shadow', shadow);
    } else {
      root.style.removeProperty('--card-border-width');
      root.style.removeProperty('--practice-card-shadow');
    }
  }, [workspaceTheme, botanical]);

  // Palette attribute, plus a cookie so the root layout paints it before
  // hydration. Skipped until a workspace is bound (scope set): before that the
  // theme is unknown, and clearing here would flash every Botanical page.
  useEffect(() => {
    if (!scope) return;
    const root = document.documentElement;
    if (botanical) root.setAttribute('data-palette', 'botanical');
    else root.removeAttribute('data-palette');
    document.cookie = `__palette=${botanical ? 'botanical' : ''}; path=/; max-age=${botanical ? 31536000 : 0}; samesite=lax`;
  }, [scope, botanical]);

  // Theme (.dark class on <html>)
  useEffect(() => {
    const root = document.documentElement;
    function applyTheme(dark: boolean) {
      if (dark) root.classList.add('dark');
      else root.classList.remove('dark');
      setIsDark(dark);
    }
    if (theme === 'system') {
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      applyTheme(mq.matches);
      const handler = (e: MediaQueryListEvent) => applyTheme(e.matches);
      mq.addEventListener('change', handler);
      return () => mq.removeEventListener('change', handler);
    }
    applyTheme(theme === 'dark');
  }, [theme]);

  // Density
  useEffect(() => {
    document.documentElement.setAttribute('data-density', density);
  }, [density]);

  // Accent color — applied in both modes (single user-chosen accent reads on
  // both light and dark surfaces; foreground stays white per --brand-primary-foreground).
  useEffect(() => {
    const root = document.documentElement;
    if (accentColor || !botanical) applyAccentColor(root, accentColor ?? '#5a6b7d');
    else ['--brand-primary', '--brand-primary-hover', '--brand-primary-light', '--brand-primary-dark', '--ring'].forEach((p) => root.style.removeProperty(p));
  }, [accentColor, botanical]);

  // Sidebar color — light-mode only. In dark mode the .dark block's
  // --sidebar-bg/--sidebar-text take over so the shell is genuinely dark.
  useEffect(() => {
    const root = document.documentElement;
    if (!isDark && sidebarColor) {
      const fg = sidebarTextColor || getContrastForeground(sidebarColor);
      root.style.setProperty('--sidebar-bg', sidebarColor);
      root.style.setProperty('--sidebar-text', fg);
      // Derive the hover/active surfaces and active-text from the chosen
      // sidebar colour. Without this a dark custom sidebar keeps the light
      // :root defaults (light hover/active bg) while --sidebar-text is white —
      // producing white-on-light, unreadable menu items and org switcher.
      root.style.setProperty(
        '--sidebar-hover',
        `color-mix(in srgb, ${sidebarColor} 90%, ${fg})`
      );
      root.style.setProperty(
        '--sidebar-active',
        `color-mix(in srgb, ${sidebarColor} 80%, ${fg})`
      );
      root.style.setProperty('--sidebar-active-text', fg);
    } else {
      root.style.removeProperty('--sidebar-bg');
      root.style.removeProperty('--sidebar-text');
      root.style.removeProperty('--sidebar-hover');
      root.style.removeProperty('--sidebar-active');
      root.style.removeProperty('--sidebar-active-text');
    }
  }, [sidebarColor, sidebarTextColor, isDark]);

  // Content background — light-mode only. .dark block's warm-charcoal
  // --background/--card apply when dark.
  useEffect(() => {
    const root = document.documentElement;
    if (!isDark && contentBgColor) {
      root.style.setProperty('--background', contentBgColor);
      root.style.setProperty('--card', `color-mix(in srgb, ${contentBgColor} 85%, white)`);
    } else {
      root.style.removeProperty('--background');
      root.style.removeProperty('--card');
    }
  }, [contentBgColor, isDark]);
}
